import data from '@emoji-mart/data';
import EmojiPicker from '@emoji-mart/react';
import {
  useFloating,
  FloatingPortal,
  FloatingArrow,
  arrow,
  autoUpdate,
  useDismiss,
} from '@floating-ui/react';
import EmojiActive from 'Assets/images/emoji-active.svg';
import EmojiOnHover from 'Assets/images/emoji-hover.svg';
import EmojiInactive from 'Assets/images/emoji-inactive.svg';
import React, { useRef } from 'react';
import { theme } from '~/styles/theme/Theme';
import { buildEmojiButtonMiddleware } from './emojiButtonFloating';
import { Styled } from './index.styles';
import { EmojiButtonProps } from './types';

export const EmojiButton: React.FC<EmojiButtonProps> = ({
  isOpen,
  onClose,
  onOpen,
  onChooseEmoji,
  relativePosition,
  children,
  placement = 'top-end',
  portalTarget = document.getElementById('root'),
  ...props
}) => {
  const arrowRef = useRef(null);

  const { refs, floatingStyles, context } = useFloating({
    transform: false,
    open: isOpen,
    onOpenChange: isOpen ? onClose : onOpen,
    whileElementsMounted: autoUpdate,
    placement,
    middleware: [
      ...buildEmojiButtonMiddleware(placement),
      arrow({
        element: arrowRef,
      }),
    ],
  });

  useDismiss(context);

  const portalRootTarget = relativePosition
    ? document.getElementById('chat-list-wrapper')
    : portalTarget;

  return (
    <Styled.EmojiWrapper
      className="emoji-wrapper"
      onKeyDown={(e) => {
        if (e.key !== 'Escape') return;
        onClose();
        e.preventDefault();
        e.stopPropagation();
      }}
      $relative={relativePosition}
      data-testid={`emojiWrapper`}
      {...props}
      ref={refs.setReference}
    >
      <FloatingPortal root={portalRootTarget}>
        <Styled.EmojiBox
          ref={refs.setFloating}
          className="emoji-box"
          data-testid={`emojiBox`}
          data-open-above={placement.startsWith('top') ? 'true' : undefined}
          style={{ ...floatingStyles }}
          aria-hidden={!isOpen}
        >
          {isOpen && (
            <>
              <FloatingArrow
                ref={arrowRef}
                context={context}
                fill={theme.colors.border}
              />
              <EmojiPicker
                data={data}
                onEmojiSelect={onChooseEmoji}
                onClose={onClose}
                autoFocus
                theme={'light'}
                icons={'outline'}
                skinTonePosition={'search'}
              />
            </>
          )}
        </Styled.EmojiBox>
      </FloatingPortal>

      <Styled.EmojiIconWrapper
        onClick={(e) => {
          isOpen ? onClose() : onOpen();
          e.preventDefault();
          e.stopPropagation();
        }}
        data-testid={`emojiIconWrapper`}
      >
        {children || (
          <img
            src={isOpen ? EmojiActive : EmojiInactive}
            onMouseOver={(e) =>
              (e.currentTarget.src = isOpen ? EmojiActive : EmojiOnHover)
            }
            onMouseLeave={(e) =>
              (e.currentTarget.src = isOpen ? EmojiActive : EmojiInactive)
            }
            data-testid={`emojiIcon`}
          />
        )}
      </Styled.EmojiIconWrapper>
    </Styled.EmojiWrapper>
  );
};
